/**
 * GMB Authentication API
 * 
 * Handles OAuth flow and account management
 */

import type { GMBWorkerResponse, OAuthResult } from '../types'

// GMB Worker base URL and token from environment
const GMB_WORKER_URL = import.meta.env.VITE_GMB_WORKER_URL || 'https://fixmybrick-gmb-worker.floral-breeze-0a38.workers.dev'
const GMB_WORKER_TOKEN = import.meta.env.VITE_GMB_WORKER_TOKEN || 'dev-token'

/**
 * Core worker API call function
 */
async function callGMBWorker<T = any>(
    action: string,
    params?: Record<string, any>
): Promise<GMBWorkerResponse<T>> {
    try {
        const token = GMB_WORKER_TOKEN
        const isDev = import.meta.env.MODE !== 'production'

        if (isDev) {
            console.log(`GMB Worker [${action}] request:`, { action, params })
        }

        const response = await fetch(GMB_WORKER_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ action, params }),
        })

        if (!response.ok) {
            const errorText = await response.text()
            if (isDev) {
                console.error(`GMB Worker [${action}] error:`, errorText)
            }
            return {
                success: false,
                error: `HTTP ${response.status}: ${response.statusText}`,
                message: `Failed to call GMB Worker action: ${action}`
            }
        }

        const result = await response.json()

        if (isDev) {
            console.log(`GMB Worker [${action}] response:`, result)
        }

        return result
    } catch (error) {
        if (import.meta.env.MODE !== 'production') {
            console.error(`GMB Worker API error [${action}]:`, error)
        }
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            message: `Failed to call GMB Worker action: ${action}`
        }
    }
}

/**
 * Generate OAuth URL for GMB authorization
 */
export async function generateOAuthUrl(): Promise<GMBWorkerResponse<OAuthResult>> {
    return callGMBWorker<OAuthResult>('oauth_url')
}

/**
 * Handle OAuth callback with authorization code
 */
export async function handleOAuthCallback(code: string, state?: string): Promise<GMBWorkerResponse> {
    return callGMBWorker('oauth_callback', { code, state })
}

/**
 * Get stored accounts and locations from cache
 */
export async function getStoredAccounts(): Promise<GMBWorkerResponse> {
    return callGMBWorker('get_stored_accounts')
}

/**
 * Check if OAuth is configured
 */
export async function checkOAuthStatus(): Promise<{
    isConfigured: boolean
    lastSync?: string
    accountCount: number
    locationCount: number
}> {
    const result = await getStoredAccounts()
    return {
        isConfigured: result.success && result.data?.accounts?.length > 0,
        lastSync: result.data?.last_sync,
        accountCount: result.data?.accounts?.length || 0,
        locationCount: result.data?.locations?.length || 0
    }
}

/**
 * List all GMB accounts
 */
export async function listAccounts(): Promise<GMBWorkerResponse<{
    accounts: Array<{
        name: string
        accountName: string
        type: string
        role: string
        state: string
        accountNumber?: string
    }>
}>> {
    return callGMBWorker('list_accounts')
}

// Export the core worker function for internal use
export { callGMBWorker }
